import React from "react";
import { twMerge } from "tailwind-merge";

const TONES = {
  info: "bg-blue-100 text-blue-800",
  blue: "bg-blue-100 text-blue-800",
  success: "bg-green-100 text-green-800",
  failure: "bg-red-100 text-red-800",
  warning: "bg-amber-100 text-amber-800",
  gray: "bg-gray-100 text-gray-800",
  purple: "bg-purple-100 text-purple-800",
} as const;

const TONES_SOLID = {
  info: "bg-blue-600 text-white",
  blue: "bg-blue-600 text-white",
  success: "bg-green-600 text-white",
  failure: "bg-red-600 text-white",
  warning: "bg-amber-500 text-amber-950",
  gray: "bg-gray-700 text-white",
  purple: "bg-purple-600 text-white",
} as const;

const BORDERS = {
  info: "border border-blue-200",
  blue: "border border-blue-200",
  success: "border border-green-200",
  failure: "border border-red-200",
  warning: "border border-amber-200",
  gray: "border border-gray-200",
  purple: "border border-purple-200",
} as const;

const DOTS = {
  info: "bg-blue-500",
  blue: "bg-blue-500",
  success: "bg-green-500",
  failure: "bg-red-500",
  warning: "bg-amber-500",
  gray: "bg-gray-500",
  purple: "bg-purple-500",
} as const;

export type BadgeTone = keyof typeof TONES;

const SIZES = { xs: "text-xs", sm: "text-sm" } as const;
const ICON_SIZES = { xs: "h-3 w-3", sm: "h-3.5 w-3.5" } as const;

export interface StatusBadgeProps {
  color?: BadgeTone;
  size?: keyof typeof SIZES;
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  dot?: boolean;
  emphasis?: "soft" | "solid";
  border?: boolean;
  shape?: "rounded" | "full";
  className?: string;
  title?: string;
  children?: React.ReactNode;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  color = "info",
  size = "xs",
  icon: Icon,
  dot = false,
  emphasis = "soft",
  border = false,
  shape,
  className,
  title,
  children,
}) => {
  const fill = emphasis === "solid" ? TONES_SOLID[color] : TONES[color];
  const legacyIcon =
    !!Icon && shape === undefined && !dot && emphasis === "soft" && !border;
  const rounded = shape ? shape === "full" : !!Icon;
  const hasText =
    children !== undefined && children !== null && children !== false;
  return (
    <span
      title={title}
      className={twMerge(
        "flex h-fit items-center gap-1 font-semibold",
        fill,
        SIZES[size],
        rounded ? "rounded-full" : "rounded",
        legacyIcon ? "p-1.5" : "px-2 py-0.5",
        border && BORDERS[color],
        title && "cursor-help",
        className
      )}
    >
      {dot && (
        <span
          aria-hidden
          className={twMerge("h-1.5 w-1.5 shrink-0 rounded-full", DOTS[color])}
        />
      )}
      {Icon && <Icon aria-hidden className={ICON_SIZES[size]} />}
      {hasText && <span>{children}</span>}
    </span>
  );
};

export default StatusBadge;
